import React, { useEffect, useMemo, useState } from 'react';

import { Card, MultiSelect, Select, Stack, Text, Title } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import chroma from 'chroma-js';
import { useTranslation } from 'react-i18next';

import { VerticalRangeSlider } from './VerticalRangeSlider';
import { type MetricBounds, type TrackPoint } from '../../types';
import { formatMetricLabel } from '../../utils/metricLabels';

import './CorrelationPanel.css';
import './themeInversion.css';

const TEXT_CONTRAST = 'var(--textContrastHigh, var(--contrast))';
const TEXT_MUTED = 'var(--textMutedStrong, var(--textMuted))';
const SURFACE_STRONG = 'var(--surfaceStrong, rgba(0, 0, 0, 0.04))';
const BORDER_COLOR = 'var(--borderSubtle, rgba(0, 0, 0, 0.08))';
const LINE_COLOR_DEFAULT = '#000000';
const TEXT_ON_SURFACE = 'rgba(0, 0, 0, 0.92)';
const SLIDER_AXIS_OFFSET = 14;
const SLIDER_WIDTH_EXPANDED = 34;
const SLIDER_WIDTH_COLLAPSED = 22;
const SLIDER_OFFSET = 28;
const SLIDER_INACTIVE_OPACITY = 0.35;
const MAX_SELECTED_METRICS = 8;
const MIN_METRICS = 2;
const RESERVED_FIELDS = new Set(['utc', 'lat', 'lon', 'globalIndex']);
const DEFAULT_PRIORITY = [
  'tws',
  'twa_abs',
  'twa',
  'vmg',
  'bsp',
  'sog',
  'heel',
  'rudder',
  'trim',
  'rot',
  'bsppc',
  'vmgpc',
];
const BRAND_COLOR_STOPS = ['#12283a', '#1f6aa5', '#f2f6fa'];
const AXIS_MARGIN = { top: 32, right: 24, bottom: 32, left: 24 };
const AXIS_TICKS = 4;
const FORMATTER = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const PANEL_MIN_WIDTH = 420;
const PANEL_MIN_HEIGHT = 360;
const PLOT_MIN_WIDTH = 360;
const PLOT_MIN_HEIGHT = 280;
const CONTROLS_MIN_WIDTH = 300;
const COLOR_SELECT_MIN_WIDTH = 240;
const LEGEND_MIN_WIDTH = 200;

type ColorByValue = string | null;
type MetricKeyWithBounds = keyof MetricBounds['min'];
type RangeTuple = [number, number];

type AxisDefinition = {
  key: string;
  label: string;
  domain: RangeTuple;
  baseDomain: RangeTuple;
  ticks: number[];
};

type Polyline = {
  id: number;
  normalized: number[];
  colorValue: number | null;
};

type ProcessedData = {
  axes: AxisDefinition[];
  polylines: Polyline[];
  colorDomain: [number, number] | null;
  sampleCount: number;
};

type BaseAxis = {
  key: string;
  label: string;
  baseDomain: RangeTuple;
};

type BaseRow = {
  id: number;
  order: number;
  trackIndex: number;
  values: number[];
};

type BaseData = {
  axes: BaseAxis[];
  rows: BaseRow[];
};

type FitResult = {
  slope: number;
  intercept: number;
  sampleCount: number;
  xRange: RangeTuple;
  yRange: RangeTuple;
  reason: 'none' | 'insufficient' | 'invalid';
};

function computeLinearFit(
  baseData: BaseData,
  ranges: Record<string, RangeTuple>,
): FitResult | null {
  try {
    if (baseData.axes.length < 2 || baseData.rows.length === 0) {
      return null;
    }
    const xAxis = baseData.axes[0];
    const yAxis = baseData.axes[1];

    const xRange = normalizeRange(ranges[xAxis.key] ?? xAxis.baseDomain, xAxis.baseDomain);
    const yRange = normalizeRange(ranges[yAxis.key] ?? yAxis.baseDomain, yAxis.baseDomain);

    const xs: number[] = [];
    const ys: number[] = [];

    for (const row of baseData.rows) {
      const x = row.values[0];
      const y = row.values[1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        continue;
      }
      if (x < xRange[0] || x > xRange[1]) {
        continue;
      }
      if (y < yRange[0] || y > yRange[1]) {
        continue;
      }
      xs.push(x);
      ys.push(y);
    }

    if (xs.length < 2) {
      return {
        slope: 0,
        intercept: 0,
        sampleCount: xs.length,
        xRange,
        yRange,
        reason: 'insufficient',
      };
    }

    const n = xs.length;
    let sumX = 0;
    let sumY = 0;
    for (let i = 0; i < n; i += 1) {
      sumX += xs[i]!;
      sumY += ys[i]!;
    }
    const meanX = sumX / n;
    const meanY = sumY / n;

    let sxx = 0;
    let sxy = 0;
    for (let i = 0; i < n; i += 1) {
      const dx = xs[i]! - meanX;
      const dy = ys[i]! - meanY;
      sxx += dx * dx;
      sxy += dx * dy;
    }

    if (sxx === 0) {
      return { slope: 0, intercept: meanY, sampleCount: n, xRange, yRange, reason: 'invalid' };
    }

    const slope = sxy / sxx;
    const intercept = meanY - slope * meanX;

    return { slope, intercept, sampleCount: n, xRange, yRange, reason: 'none' };
  } catch {
    return null;
  }
}

function buildRegressionPath(
  fit: FitResult,
  axes: AxisDefinition[],
  chartHeight: number,
  axisSpacing: number,
): string | null {
  if (axes.length < 2) {
    return null;
  }
  const yAxis = axes[1];

  // Evaluate fit at the ends of the current X range
  const x0 = fit.xRange[0];
  const x1 = fit.xRange[1];
  const y0 = fit.slope * x0 + fit.intercept;
  const y1 = fit.slope * x1 + fit.intercept;

  // Clamp Y to visible range
  const y0Clamped = clampToDomain(y0, yAxis.domain);
  const y1Clamped = clampToDomain(y1, yAxis.domain);

  const y0Norm = normalizeToUnitDomain(y0Clamped, yAxis.domain);
  const y1Norm = normalizeToUnitDomain(y1Clamped, yAxis.domain);

  const xSvg0 = AXIS_MARGIN.left + 0 * axisSpacing;
  const xSvg1 = AXIS_MARGIN.left + 1 * axisSpacing;
  const ySvg0 = AXIS_MARGIN.top + chartHeight - y0Norm * chartHeight;
  const ySvg1 = AXIS_MARGIN.top + chartHeight - y1Norm * chartHeight;

  // Only draw if X range is valid within visible domain
  if (!Number.isFinite(ySvg0) || !Number.isFinite(ySvg1)) {
    return null;
  }

  return `M${xSvg0},${ySvg0} L${xSvg1},${ySvg1}`;
}

const CONTROL_STYLES = {
  label: {
    color: TEXT_CONTRAST,
    fontWeight: 600,
    letterSpacing: '0.01em',
  },
  input: {
    color: TEXT_CONTRAST,
    backgroundColor: 'var(--surface, rgba(15, 23, 42, 0.04))',
    borderColor: BORDER_COLOR,
  },
  dropdown: {
    backgroundColor: 'var(--surfaceStrong, rgba(15, 23, 42, 0.08))',
    color: TEXT_ON_SURFACE,
    backdropFilter: 'blur(12px)',
    border: `1px solid ${BORDER_COLOR}`,
  },
  item: {
    color: TEXT_ON_SURFACE,
    '&[data-selected]': {
      backgroundColor: 'rgba(148, 197, 255, 0.28)',
      color: TEXT_ON_SURFACE,
    },
  },
  searchInput: {
    color: TEXT_CONTRAST,
  },
} as const;

function clampToDomain(value: number, [min, max]: RangeTuple): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
}

function normalizeRange(range: RangeTuple, domain: RangeTuple): RangeTuple {
  const [rawMin, rawMax] = range;
  const [domainMin, domainMax] = domain;
  const minClamped = clampToDomain(Math.min(rawMin, rawMax), domain);
  const maxClamped = clampToDomain(Math.max(rawMin, rawMax), domain);
  if (minClamped === maxClamped) {
    const epsilon = (domainMax - domainMin) * 0.01 || 1;
    const adjustedMax = clampToDomain(maxClamped + epsilon, domain);
    if (adjustedMax !== maxClamped) {
      return [minClamped, adjustedMax];
    }
    const adjustedMin = clampToDomain(minClamped - epsilon, domain);
    return [adjustedMin, maxClamped];
  }
  return [minClamped, maxClamped];
}

function normalizeToUnitDomain(value: number, [domainMin, domainMax]: RangeTuple): number {
  const span = domainMax - domainMin;
  if (span <= 0) {
    return 0.5;
  }
  const normalized = (value - domainMin) / span;
  if (!Number.isFinite(normalized)) {
    return 0.5;
  }
  return Math.min(1, Math.max(0, normalized));
}

function computeSliderStep([min, max]: RangeTuple): number {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return 1;
  }
  const span = Math.abs(max - min);
  if (span === 0) {
    return 1;
  }
  const rough = span / 200;
  const exponent = Math.pow(10, Math.floor(Math.log10(rough || 1)));
  const normalized = rough / exponent;
  const stepBase = normalized >= 5 ? 5 : normalized >= 2 ? 2 : 1;
  const step = stepBase * exponent;
  return step > 0 ? step : span / 100 || 1;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function coerceNumber(value: unknown): number | null {
  if (isFiniteNumber(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function hasBounds(
  metricBounds: MetricBounds | undefined,
  metric: string,
): metric is MetricKeyWithBounds {
  if (!metricBounds) {
    return false;
  }
  return Object.prototype.hasOwnProperty.call(metricBounds.min, metric);
}

function buildTicks(min: number, max: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [];
  }
  if (min === max) {
    return [min];
  }
  const ticks: number[] = [];
  const step = (max - min) / AXIS_TICKS;
  for (let index = 0; index <= AXIS_TICKS; index += 1) {
    ticks.push(min + step * index);
  }
  return ticks;
}

function ensureMetricCount(metrics: string[], available: string[]): string[] {
  const filtered = metrics.filter((metric) => available.includes(metric));
  if (filtered.length >= MIN_METRICS) {
    return filtered.slice(0, MAX_SELECTED_METRICS);
  }
  const fallback = available.slice(0, Math.max(MIN_METRICS, filtered.length));
  return [...new Set([...filtered, ...fallback])].slice(0, MAX_SELECTED_METRICS);
}

interface CorrelationPanelProperties {
  trackData: TrackPoint[];
  metricBounds?: MetricBounds;
}

export const CorrelationPanel: React.FC<CorrelationPanelProperties> = ({
  trackData,
  metricBounds,
}) => {
  const { t } = useTranslation('common');
  const plotSize = useElementSize<HTMLDivElement>();
  const { width, height, ref: measurePlotRef } = plotSize;

  const availableMetrics = useMemo(() => {
    const metrics = new Set<string>();
    for (const point of trackData) {
      if (!point || typeof point !== 'object') {
        continue;
      }
      for (const [key, value] of Object.entries(point)) {
        if (RESERVED_FIELDS.has(key)) {
          continue;
        }
        if (coerceNumber(value) === null) {
          continue;
        }
        metrics.add(key);
      }
    }
    return [...metrics].sort((a, b) => a.localeCompare(b));
  }, [trackData]);

  const defaultMetrics = useMemo(() => {
    const prioritized = DEFAULT_PRIORITY.filter((metric) => availableMetrics.includes(metric));
    const remainder = availableMetrics.filter((metric) => !prioritized.includes(metric));
    return [...prioritized, ...remainder].slice(0, Math.min(4, availableMetrics.length));
  }, [availableMetrics]);

  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(defaultMetrics);
  const [colorBy, setColorBy] = useState<ColorByValue>(null);
  const [metricRanges, setMetricRanges] = useState<Record<string, RangeTuple>>({});
  const [activeSlider, setActiveSlider] = useState<string | null>(null);

  useEffect(() => {
    setSelectedMetrics((current) => ensureMetricCount(current, availableMetrics));
  }, [availableMetrics]);

  useEffect(() => {
    if (colorBy && !availableMetrics.includes(colorBy)) {
      setColorBy(null);
    }
  }, [availableMetrics, colorBy]);

  const metricOptions = useMemo(
    () => availableMetrics.map((metric) => ({ value: metric, label: formatMetricLabel(metric) })),
    [availableMetrics],
  );

  const colorOptions = useMemo(
    () =>
      availableMetrics.map((metric) => ({
        value: metric,
        label: formatMetricLabel(metric),
      })),
    [availableMetrics],
  );

  useEffect(() => {
    if (colorOptions.length === 0) {
      setColorBy(null);
      return;
    }
    setColorBy((current) => {
      if (current && colorOptions.some((option) => option.value === current)) {
        return current;
      }
      return colorOptions[0]?.value ?? null;
    });
  }, [colorOptions]);

  const baseData = useMemo<BaseData>(() => {
    if (selectedMetrics.length < MIN_METRICS || trackData.length === 0) {
      return { axes: [], rows: [] };
    }

    const axisStats = selectedMetrics.map(() => ({
      min: Number.POSITIVE_INFINITY,
      max: Number.NEGATIVE_INFINITY,
    }));

    const rows: BaseRow[] = [];
    let order = 0;

    for (const [trackIndex, point] of trackData.entries()) {
      const values: number[] = [];
      let valid = true;

      for (const [axisIndex, metric] of selectedMetrics.entries()) {
        const raw = (point as unknown as Record<string, unknown>)[metric];
        const value = coerceNumber(raw);
        if (value === null) {
          valid = false;
          break;
        }
        values.push(value);
        const stats = axisStats[axisIndex];
        stats.min = Math.min(stats.min, value);
        stats.max = Math.max(stats.max, value);
      }

      if (!valid) {
        continue;
      }

      rows.push({
        id: order,
        order,
        trackIndex,
        values,
      });
      order += 1;
    }

    if (rows.length === 0) {
      return { axes: [], rows: [] };
    }

    const axes: BaseAxis[] = [];
    for (const [axisIndex, metric] of selectedMetrics.entries()) {
      const stats = axisStats[axisIndex];
      if (!Number.isFinite(stats.min) || !Number.isFinite(stats.max)) {
        continue;
      }
      let domainMin = stats.min;
      let domainMax = stats.max;
      if (metricBounds && hasBounds(metricBounds, metric)) {
        const typed = metric as MetricKeyWithBounds;
        const lower = metricBounds.iqrMin?.[typed] ?? metricBounds.min[typed];
        const upper = metricBounds.iqrMax?.[typed] ?? metricBounds.max[typed];
        if (Number.isFinite(lower)) {
          domainMin = Math.min(domainMin, Number(lower));
        }
        if (Number.isFinite(upper)) {
          domainMax = Math.max(domainMax, Number(upper));
        }
      }
      if (domainMin === domainMax) {
        domainMax = domainMin + 1;
      }
      axes.push({
        key: metric,
        label: formatMetricLabel(metric),
        baseDomain: [domainMin, domainMax],
      });
    }

    return { axes, rows };
  }, [selectedMetrics, trackData, metricBounds]);

  const processed = useMemo<ProcessedData>(() => {
    if (baseData.axes.length === 0 || baseData.rows.length === 0) {
      return { axes: [], polylines: [], colorDomain: null, sampleCount: 0 };
    }

    const axes: AxisDefinition[] = [];
    const rangeByKey: Record<string, RangeTuple> = {};

    for (const axis of baseData.axes) {
      const override = metricRanges[axis.key];
      const domain = normalizeRange(override ?? axis.baseDomain, axis.baseDomain);
      rangeByKey[axis.key] = domain;
      axes.push({
        key: axis.key,
        label: axis.label,
        baseDomain: axis.baseDomain,
        domain,
        ticks: buildTicks(domain[0], domain[1]),
      });
    }

    const polylines: Polyline[] = [];
    let colorMin = Number.POSITIVE_INFINITY;
    let colorMax = Number.NEGATIVE_INFINITY;

    for (const row of baseData.rows) {
      let include = true;
      for (const [axisIndex, axis] of axes.entries()) {
        const range = rangeByKey[axis.key];
        const value = row.values[axisIndex];
        if (value < range[0] || value > range[1]) {
          include = false;
          break;
        }
      }
      if (!include) {
        continue;
      }

      const normalized = axes.map((axis, axisIndex) => {
        const [domainMin, domainMax] = axis.domain;
        const span = domainMax - domainMin;
        if (span <= 0) {
          return 0.5;
        }
        const raw = row.values[axisIndex];
        return Math.min(1, Math.max(0, (raw - domainMin) / span));
      });

      let colorValue: number | null = null;
      if (colorBy) {
        const point = trackData[row.trackIndex] as unknown as Record<string, unknown> | undefined;
        const candidate = point ? coerceNumber(point[colorBy]) : null;
        colorValue = candidate ?? row.order;
      } else {
        colorValue = row.order;
      }

      if (colorValue !== null) {
        colorMin = Math.min(colorMin, colorValue);
        colorMax = Math.max(colorMax, colorValue);
      }

      polylines.push({
        id: row.id,
        normalized,
        colorValue,
      });
    }

    let colorDomain: [number, number] | null = null;
    if (polylines.length > 0 && Number.isFinite(colorMin) && Number.isFinite(colorMax)) {
      colorDomain = [colorMin, colorMax] as [number, number];
    }

    return {
      axes,
      polylines,
      colorDomain,
      sampleCount: polylines.length,
    };
  }, [baseData.axes, baseData.rows, metricRanges, colorBy, trackData]);

  useEffect(() => {
    if (activeSlider && !processed.axes.some((axis) => axis.key === activeSlider)) {
      setActiveSlider(null);
    }
  }, [activeSlider, processed.axes]);

  useEffect(() => {
    if (baseData.axes.length === 0) {
      setMetricRanges((current) => (Object.keys(current).length > 0 ? {} : current));
      return;
    }
    setMetricRanges((current) => {
      const next: Record<string, RangeTuple> = {};
      let changed = false;
      for (const axis of baseData.axes) {
        const existing = current[axis.key];
        const normalized = normalizeRange(existing ?? axis.baseDomain, axis.baseDomain);
        next[axis.key] = normalized;
        if (!existing || existing[0] !== normalized[0] || existing[1] !== normalized[1]) {
          changed = true;
        }
      }
      if (Object.keys(next).length !== Object.keys(current).length) {
        changed = true;
      }
      if (!changed) {
        return current;
      }
      return next;
    });
  }, [baseData.axes]);

  const colorInterpolator = useMemo(() => {
    if (!processed.colorDomain || !colorBy) {
      return null;
    }
    const [min, max] = processed.colorDomain;
    if (min === max) {
      const fallbackColor = BRAND_COLOR_STOPS.at(-1) ?? LINE_COLOR_DEFAULT;
      return () => chroma(fallbackColor);
    }
    return chroma.scale(BRAND_COLOR_STOPS).domain([min, max]);
  }, [processed.colorDomain, colorBy]);

  const shouldObservePlotSize = processed.axes.length > 0;
  const plotReference = shouldObservePlotSize ? measurePlotRef : undefined;

  const rightMargin = useMemo(
    () => Math.max(AXIS_MARGIN.right, SLIDER_OFFSET + SLIDER_WIDTH_EXPANDED + 8),
    [],
  );

  const measuredWidth = shouldObservePlotSize ? width : 0;
  const measuredHeight = shouldObservePlotSize ? height : 0;

  const chartWidth = Math.max(measuredWidth - AXIS_MARGIN.left - rightMargin, 0);
  const chartHeight = Math.max(measuredHeight - AXIS_MARGIN.top - AXIS_MARGIN.bottom, 0);
  const axisSpacing = processed.axes.length > 1 ? chartWidth / (processed.axes.length - 1) : 0;

  const colorLabel = useMemo(() => {
    if (!colorBy) {
      return null;
    }
    return formatMetricLabel(colorBy);
  }, [colorBy]);

  const colorRange = processed.colorDomain;
  const showLegend = Boolean(colorBy && colorLabel && colorRange);
  const legendData = showLegend
    ? {
        label: colorLabel as string,
        min: colorRange![0],
        max: colorRange![1],
      }
    : null;

  return (
    <Card
      className="correlation-panel theme-inverted"
      withBorder
      radius="md"
      p="md"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minWidth: PANEL_MIN_WIDTH,
        minHeight: PANEL_MIN_HEIGHT,
      }}
    >
      <Stack gap="sm">
        <div className="correlation-panel__top-row">
          <div
            className="correlation-panel__controls correlation-panel__controls--metrics"
            style={{ minWidth: CONTROLS_MIN_WIDTH }}
          >
            <MultiSelect
              label={t('race.correlation.metricsLabel', 'Metrics')}
              data={metricOptions}
              value={selectedMetrics}
              onChange={(values) => setSelectedMetrics(values.slice(0, MAX_SELECTED_METRICS))}
              searchable
              nothingFoundMessage={t('race.correlation.noMetrics', 'No numeric metrics available')}
              placeholder={t('race.correlation.metricsPlaceholder', 'Add metrics')}
              styles={CONTROL_STYLES}
            />
          </div>
        </div>

        <div className="correlation-panel__color-row">
          <div
            className="correlation-panel__color-select"
            style={{ minWidth: COLOR_SELECT_MIN_WIDTH }}
          >
            <Select
              label={t('race.correlation.colorByLabel', 'Color by')}
              data={colorOptions}
              value={colorBy ?? null}
              onChange={(value) => setColorBy(value ?? null)}
              styles={CONTROL_STYLES}
              placeholder={t('race.correlation.colorByPlaceholder', 'Select metric')}
              disabled={colorOptions.length === 0}
              clearable={false}
            />
          </div>
          {legendData && (
            <div
              className="correlation-panel__legend correlation-panel__legend--top"
              style={{ minWidth: LEGEND_MIN_WIDTH }}
            >
              <Text size="xs" c="dimmed" className="correlation-panel__legend-label">
                {legendData.label}
              </Text>
              <div
                className="correlation-panel__gradient"
                style={{
                  backgroundImage: `linear-gradient(90deg, ${BRAND_COLOR_STOPS.join(', ')})`,
                }}
              />
              <Text size="xs" c="dimmed">
                {FORMATTER.format(legendData.min)}
              </Text>
              <Text size="xs" c="dimmed">
                {FORMATTER.format(legendData.max)}
              </Text>
            </div>
          )}
        </div>
      </Stack>

      <div
        className="correlation-panel__plot"
        ref={plotReference}
        style={{ minWidth: PLOT_MIN_WIDTH, minHeight: PLOT_MIN_HEIGHT }}
      >
        {processed.axes.length > 0 && chartWidth > 0 && chartHeight > 0 ? (
          <>
            <svg className="correlation-panel__svg" role="img">
              {processed.axes.map((axis, index) => {
                const x = AXIS_MARGIN.left + axisSpacing * index;
                return (
                  <g key={axis.key} transform={`translate(${x}, ${AXIS_MARGIN.top})`}>
                    <line
                      x1={0}
                      y1={0}
                      x2={0}
                      y2={chartHeight}
                      stroke={BORDER_COLOR}
                      strokeWidth={1}
                    />
                    <text
                      x={0}
                      y={-12}
                      textAnchor="middle"
                      fill={TEXT_CONTRAST}
                      className="correlation-panel__axis-label"
                    >
                      {axis.label}
                    </text>
                    {axis.ticks.map((tick) => {
                      const span = axis.domain[1] - axis.domain[0];
                      const normalized = span <= 0 ? 0.5 : (tick - axis.domain[0]) / span;
                      const y = chartHeight - normalized * chartHeight;
                      return (
                        <g key={`${axis.key}-${tick}`}>
                          <line
                            x1={-6}
                            x2={6}
                            y1={y}
                            y2={y}
                            stroke={BORDER_COLOR}
                            strokeWidth={1}
                          />
                          <text
                            x={-10}
                            y={y + 4}
                            textAnchor="end"
                            fill={TEXT_MUTED}
                            className="correlation-panel__tick-label"
                          >
                            {FORMATTER.format(tick)}
                          </text>
                        </g>
                      );
                    })}
                  </g>
                );
              })}

              {processed.polylines.map((polyline) => {
                const path = polyline.normalized
                  .map((value, index) => {
                    const x = AXIS_MARGIN.left + axisSpacing * index;
                    const y = AXIS_MARGIN.top + chartHeight - value * chartHeight;
                    return `${index === 0 ? 'M' : 'L'}${x},${y}`;
                  })
                  .join(' ');
                const stroke = colorInterpolator
                  ? colorInterpolator(polyline.colorValue ?? colorRange?.[0] ?? 0).hex()
                  : LINE_COLOR_DEFAULT;

                return (
                  <path
                    key={polyline.id}
                    d={path}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={1}
                    opacity={0.55}
                  />
                );
              })}
              {/* Regression line for first two metrics (rendered on top) */}
              {processed.axes.length >= 2 &&
                chartWidth > 0 &&
                chartHeight > 0 &&
                (() => {
                  const fit = computeLinearFit(baseData, metricRanges);
                  if (!fit || fit.reason !== 'none') {
                    return null;
                  }
                  const pathD = buildRegressionPath(fit, processed.axes, chartHeight, axisSpacing);
                  if (!pathD) {
                    return null;
                  }
                  const stroke = '#1f6aa5';
                  return (
                    <path
                      d={pathD}
                      fill="none"
                      stroke={stroke}
                      strokeWidth={2.5}
                      opacity={0.95}
                      pointerEvents="none"
                    />
                  );
                })()}
            </svg>
            <div className="correlation-panel__sliders">
              {processed.axes.map((axis, index) => {
                const x = AXIS_MARGIN.left + axisSpacing * index;
                const sliderRange = metricRanges[axis.key] ?? axis.domain;
                const step = computeSliderStep(axis.baseDomain);
                const isActive = activeSlider === axis.key;
                const sliderWidth = isActive ? SLIDER_WIDTH_EXPANDED : SLIDER_WIDTH_COLLAPSED;
                const sliderOpacity = isActive ? 1 : SLIDER_INACTIVE_OPACITY;
                const sliderSafePadding = 12;
                const sliderCenterTarget = x + SLIDER_AXIS_OFFSET;
                const sliderLeftTarget = sliderCenterTarget - sliderWidth / 2;
                const sliderLeftMin = sliderSafePadding;
                const sliderLeftMax = Math.max(
                  sliderLeftMin,
                  width - sliderWidth - sliderSafePadding,
                );
                const sliderLeft =
                  width > 0
                    ? Math.min(Math.max(sliderLeftTarget, sliderLeftMin), sliderLeftMax)
                    : sliderLeftTarget;
                return (
                  <div
                    key={`${axis.key}-slider`}
                    className="correlation-panel__slider"
                    onMouseEnter={() => setActiveSlider(axis.key)}
                    onMouseLeave={() =>
                      setActiveSlider((current) => (current === axis.key ? null : current))
                    }
                    onFocusCapture={() => setActiveSlider(axis.key)}
                    onBlurCapture={(event) => {
                      const related = event.relatedTarget as Node | null;
                      if (!event.currentTarget.contains(related)) {
                        setActiveSlider((current) => (current === axis.key ? null : current));
                      }
                    }}
                    style={{
                      left: `${sliderLeft}px`,
                      width: `${sliderWidth}px`,
                      top: `${AXIS_MARGIN.top}px`,
                      height: `${chartHeight}px`,
                      opacity: sliderOpacity,
                      zIndex: isActive ? 2 : 1,
                      transition: 'width 160ms ease, left 160ms ease, opacity 160ms ease',
                    }}
                  >
                    <VerticalRangeSlider
                      min={axis.baseDomain[0]}
                      max={axis.baseDomain[1]}
                      step={step}
                      value={sliderRange}
                      sizeHint={{ width: sliderWidth, height: chartHeight }}
                      onChange={(range) =>
                        setMetricRanges((current) => {
                          const normalized = normalizeRange(range, axis.baseDomain);
                          const previous = current[axis.key];
                          if (
                            previous &&
                            previous[0] === normalized[0] &&
                            previous[1] === normalized[1]
                          ) {
                            return current;
                          }
                          return {
                            ...current,
                            [axis.key]: normalized,
                          };
                        })
                      }
                      style={{ height: '100%', width: '100%' }}
                    />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div
            className="correlation-panel__placeholder"
            style={{ minWidth: PLOT_MIN_WIDTH, minHeight: PLOT_MIN_HEIGHT }}
          >
            <Stack gap="xs" align="center">
              <Title order={5} style={{ color: TEXT_CONTRAST }}>
                {t('race.correlation.emptyTitle', 'No data to display')}
              </Title>
              <Text size="sm" c="dimmed" ta="center">
                {t(
                  'race.correlation.emptySubtitle',
                  'Pick at least two numeric metrics with valid samples to render the correlation plot.',
                )}
              </Text>
            </Stack>
          </div>
        )}
      </div>
    </Card>
  );
};

export default CorrelationPanel;
